import type { DataSourceProvider, DataSourceResult } from "./types"

/** Shape of the response from the BRREG Enhetsregisteret direct lookup API. */
interface BrregEnhet {
  organisasjonsnummer: string
  navn: string
  forretningsadresse?: {
    adresse: string[]
    postnummer: string
    poststed: string
  }
}

/** Shape of the response from the BRREG Enhetsregisteret search API. */
interface BrregSearchResult {
  _embedded?: {
    enheter: BrregEnhet[]
  }
}

/**
 * Format a BRREG company record into a flat DataSourceResult.
 */
function formatEnhet(enhet: BrregEnhet): DataSourceResult {
  const adresseParts: string[] = []
  if (enhet.forretningsadresse) {
    const { adresse, postnummer, poststed } = enhet.forretningsadresse
    const lines = adresse.filter(Boolean)
    if (lines.length) adresseParts.push(lines.join(", "))
    if (postnummer && poststed) adresseParts.push(`${postnummer} ${poststed}`)
  }

  return {
    organisasjonsnummer: enhet.organisasjonsnummer,
    navn: enhet.navn,
    adresse: adresseParts.join(", ") || undefined,
  }
}

/**
 * BRREG (Brønnøysundregistrene) company registry provider.
 *
 * Supports two lookup modes:
 * - **Org number**: If the query contains exactly 9 digits, performs a direct
 *   lookup by organisation number (`/enheter/{orgNr}`).
 * - **Name**: Otherwise, searches by company name
 *   (`/enheter?konkurs=false&navn={query}`). To avoid overwriting user input
 *   with a wrong guess, a result is only returned when there is exactly one
 *   match **or** one of the results is an exact (case-insensitive) name match.
 *
 * API documentation: https://data.brreg.no/enhetsregisteret/api/docs/index.html
 */
export const brregProvider: DataSourceProvider = {
  async fetch(query: string): Promise<DataSourceResult | null> {
    if (!query || query.trim().length < 3) return null

    const digits = query.replace(/\D/g, "")
    const isOrgNumber = digits.length === 9

    const url = isOrgNumber
      ? `https://data.brreg.no/enhetsregisteret/api/enheter/${digits}`
      : `https://data.brreg.no/enhetsregisteret/api/enheter?konkurs=false&navn=${encodeURIComponent(query.trim())}`

    const response = await fetch(url, {
      headers: { Accept: "application/json" },
    })

    if (!response.ok) return null

    if (isOrgNumber) {
      const enhet = (await response.json()) as BrregEnhet
      return formatEnhet(enhet)
    }

    const data = (await response.json()) as BrregSearchResult
    const enheter = data._embedded?.enheter
    if (!enheter?.length) return null

    // Single result — safe to use directly.
    if (enheter.length === 1) return formatEnhet(enheter[0])

    // Multiple results — only return if one is an exact name match,
    // otherwise the first result is likely wrong for common search terms.
    const normalised = query.trim().toLowerCase()
    const exactMatch = enheter.find((e) => e.navn.toLowerCase() === normalised)
    return exactMatch ? formatEnhet(exactMatch) : null
  },
}
